import React, { useState, useRef, useEffect, useMemo } from 'react';
import Big from 'big.js';
import { formatAmount } from '@/utils/amount';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Chip,
  TextField,
  Tooltip,
  Box,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Appeal, Transaction, Trader, Status } from '../../../types';
import { appealsService, transactionsService } from '../../../services';
import { useNotification } from '../../../contexts/NotificationContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';
import { hasPermission, Permission } from '../../../auth/permissions';
import { RoleEnum } from '../../../types/auth/roles';
import { CopyableText } from '@/utils/copy';
import { formatApiDateTime, formatApiDateTimeInTimeZone } from '@/utils/dateTime';
import {
  getAppealFileExtension,
  getAppealFileKind,
  getAppealFiles,
  type AppealFileEntry,
} from '@/utils/appealFiles';

const getStatusColor = (status: Status) => {
  switch (status) {
    case Status.SUCCESS:
      return 'success';
    case Status.DECLINED:
      return 'error';
    case Status.ACCEPTED:
      return 'warning';
    case Status.APPEAL:
      return 'info';
    case Status.NOT_FOUND:
      return 'default';
    default:
      return 'default';
  }
};

const isSandboxTransaction = (transaction: Transaction) => {
  try {
    return JSON.parse(transaction.extra_info || '{}').sandbox === true;
  } catch {
    return false;
  }
};

type TransactionAppealFileEntry = AppealFileEntry & { appealId: number };

interface TransactionDetailsDialogProps {
  transaction: Transaction | null;
  open: boolean;
  onClose: () => void;
  merchants: { id: string; name: string | null }[];
  traders: Trader[];
  onUpdate?: () => void;
  loadData: () => void;
}

const TransactionDetailsDialog: React.FC<TransactionDetailsDialogProps> = ({
  transaction,
  open,
  onClose,
  merchants,
  traders,
  onUpdate,
  loadData,
}) => {
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [callbackDialogOpen, setCallbackDialogOpen] = useState(false);
  const [amount, setAmount] = useState<number>(0);
  const [amountError, setAmountError] = useState<string>('');
  const [callbackUrl, setCallbackUrl] = useState<string>('');
  const [updateType, setUpdateType] = useState<'success' | 'decline'>('success');
  const [appealFiles, setAppealFiles] = useState<Appeal[]>([]);
  const [appealFilesLoading, setAppealFilesLoading] = useState(false);
  const [appealFilesError, setAppealFilesError] = useState(false);
  const [previewFile, setPreviewFile] = useState<TransactionAppealFileEntry | null>(null);
  const [previewFileFailed, setPreviewFileFailed] = useState(false);
  const { showNotification } = useNotification();
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isAdmin = currentUser?.role === RoleEnum.ADMIN;

  const canUpdateToDecline = useMemo(() => canPerformAction(currentUser, ActionKey.TRANSACTIONS_UPDATE_TO_DECLINE), [currentUser]);
  const canUpdateToSuccess = useMemo(
    () => canPerformAction(currentUser, transaction?.direction === 'OUT'
      ? ActionKey.TRANSACTIONS_UPDATE_TO_SUCCESS
      : ActionKey.TRANSACTIONS_IN_UPDATE_TO_SUCCESS),
    [currentUser, transaction?.direction],
  );
  const canSendCallback = canPerformAction(currentUser, ActionKey.TRANSACTIONS_SEND_CALLBACK);
  const canViewAppealFiles = Boolean(
    currentUser?.role === RoleEnum.ADMIN || currentUser?.role === RoleEnum.SUPPORT
    || hasPermission(currentUser, Permission.APPEALS_SUPPORT),
  );
  const transactionId = transaction?.id;
  const appealFileEntries = useMemo(
    () => appealFiles.flatMap((appeal) => (
      getAppealFiles(appeal).map((entry) => ({ ...entry, appealId: appeal.id }))
    )),
    [appealFiles],
  );

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !transactionId || !canViewAppealFiles) {
      setAppealFiles([]);
      setAppealFilesLoading(false);
      setAppealFilesError(false);
      setPreviewFile(null);
      setPreviewFileFailed(false);
      return;
    }

    let cancelled = false;
    setAppealFilesLoading(true);
    setAppealFilesError(false);
    setAppealFiles([]);

    appealsService.getTransactionFiles(transactionId)
      .then((files) => {
        if (!cancelled) setAppealFiles(files);
      })
      .catch(() => {
        if (!cancelled) setAppealFilesError(true);
      })
      .finally(() => {
        if (!cancelled) setAppealFilesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canViewAppealFiles, open, transactionId]);

  if (!transaction) return null;
  const isSandbox = isSandboxTransaction(transaction);
  const previewFileKind = previewFile
    ? getAppealFileKind(previewFile.file, previewFile.key)
    : 'download';

  const closeFilePreview = () => {
    setPreviewFile(null);
    setPreviewFileFailed(false);
  };

  const canManageSandbox =
    !!currentUser && (
      currentUser.is_superuser ||
      currentUser.role === RoleEnum.ADMIN ||
      currentUser.role === RoleEnum.SUPPORT
    );

  const hasPermissionToUpdateToSuccess = () => {
    return canUpdateToSuccess && transaction.status !== Status.SUCCESS;
  };

  const canUpdateToSuccessNow = hasPermissionToUpdateToSuccess();

  const merchant = merchants.find((m) => m.id === transaction.merchant_id);
  const trader = traders.find((t) => t.id === transaction.trader_id);
  const currencyCode = transaction.currency_code;
  const renderMoney = (value: string | number | null | undefined) => {
    let usdt: Big | null = null;
    try {
      const rate = currencyCode?.toUpperCase() === 'USDT' ? new Big(1)
        : transaction.usdt_rate != null ? new Big(transaction.usdt_rate) : null;
      if (currencyCode && rate?.gt(0)) usdt = new Big(value ?? 0).div(rate);
    } catch {
      usdt = null;
    }
    return <>
      <Typography variant="body1">{formatAmount(value)} {currencyCode || '(валюта неизвестна)'}</Typography>
      <Typography variant="body2" color="text.secondary">
        {usdt !== null ? `≈ ${formatAmount(usdt)} USDT` : 'USDT: нет курса для пересчёта'}
      </Typography>
    </>;
  };

  const validateAmount = (value: number): boolean => {
    if (value <= 0) {
      setAmountError('Сумма должна быть больше 0');
      return false;
    }
    if (value > 1000000000) {
      setAmountError('Сумма не может быть больше 1 000 000 000');
      return false;
    }
    setAmountError('');
    return true;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setAmount(value);
    validateAmount(value);
  };

  const handleUpdate = async () => {
    const hasPermission = isSandbox
      ? updateType === 'success'
        ? canUpdateToSuccess
        : canManageSandbox
      : updateType === 'success'
        ? hasPermissionToUpdateToSuccess()
        : canUpdateToDecline;
    if (!hasPermission) {
      showNotification('У вас нет прав для выполнения этой операции', 'error');
      return;
    }
    if (!isSandbox && !validateAmount(amount)) {
      return;
    }
    try {
      if (isSandbox) {
        await transactionsService.finalizeSandboxTransaction(
          transaction.id,
          updateType === 'success' ? 'SUCCESS' : 'DECLINED',
        );
      } else if (updateType === 'success') {
        await transactionsService.updateTransactionToSuccess(transaction.id, amount);
      } else {
        await transactionsService.updateTransactionToDecline(transaction.id, amount);
      }
      showNotification('Статус транзакции успешно обновлен', 'success');
      setUpdateDialogOpen(false);
      onUpdate?.();
      loadData();
    } catch (error) {
      showNotification('Ошибка при обновлении статуса', 'error');
    }
  };

  const handleOpenUpdateDialog = (type: 'success' | 'decline') => {
    const hasPermission = isSandbox
      ? type === 'success'
        ? canUpdateToSuccess
        : canManageSandbox
      : type === 'success'
        ? hasPermissionToUpdateToSuccess()
        : canUpdateToDecline;
    if (!hasPermission) {
      showNotification('У вас нет прав для выполнения этой операции', 'error');
      return;
    }
    setUpdateType(type);
    setAmount(transaction.amount);
    setUpdateDialogOpen(true);
  };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setPreviewFile(null);
    onClose();
  };

  const handleCloseUpdateDialog = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setUpdateDialogOpen(false);
  };

  const handleOpenCallbackDialog = () => {
    if (!canSendCallback) {
      showNotification('У вас нет прав для отправки колбэков', 'error');
      return;
    }
    setCallbackUrl(isAdmin ? transaction.callback_url || '' : '');
    setCallbackDialogOpen(true);
  };

  const handleCloseCallbackDialog = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setCallbackDialogOpen(false);
  };

  const handleSendCallback = async () => {
    if (!canSendCallback) {
      showNotification('У вас нет прав для отправки колбэков', 'error');
      return;
    }
    try {
      await transactionsService.sendCallback(
        transaction.id,
        isAdmin ? callbackUrl : undefined,
      );
      showNotification('Колбэк успешно отправлен', 'success');
      setCallbackDialogOpen(false);
      onUpdate?.();
      loadData();
    } catch (error) {
      showNotification('Ошибка при отправке колбэка', 'error');
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        disableRestoreFocus
        disableEnforceFocus
      >
        <DialogTitle>Детали транзакции</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                ID транзакции
              </Typography>
              <CopyableText
                value={String(transaction.id)}
                copiedMessage="ID транзакции скопирован"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Статус
              </Typography>
              <Chip
                label={transaction.status}
                color={getStatusColor(transaction.status)}
                size="small"
                sx={{ 
                  minWidth: '100px',
                  justifyContent: 'center'
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Мерчант
              </Typography>
              <Typography variant="body1">{merchant?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Трейдер
              </Typography>
              <Typography variant="body1">{trader?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Направление
              </Typography>
              <Typography variant="body1">{transaction.direction}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Сумма
              </Typography>
              {renderMoney(transaction.amount)}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Ставка мерчанта
              </Typography>
              <Typography variant="body1">{Number(transaction.merchant_rate).toFixed(2)}%</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Доход трейдера
              </Typography>
              {renderMoney(transaction.trader_income)}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Реф % TL
              </Typography>
              <Typography variant="body1">{Number(transaction.referral_percent || 0).toFixed(2)}%</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Доход агента
              </Typography>
              {renderMoney(transaction.referral_income || 0)}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Наш доход
              </Typography>
              {renderMoney(transaction.our_income)}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Сумма после комиссии
              </Typography>
              {renderMoney(transaction.amount_after_commission)}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Эквивалент в USDT рассчитан по текущему курсу системы.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                ID транзакции клиента
              </Typography>
              <CopyableText
                value={transaction.client_transaction_id || ''}
                copiedMessage="ID транзакции клиента скопирован"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                ID пользователя клиента
              </Typography>
              <CopyableText
                value={transaction.client_user_id || ''}
                copiedMessage="ID пользователя клиента скопирован"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Выданный реквизит
              </Typography>
              {transaction.issued_requisite ? (
                <CopyableText
                  value={transaction.issued_requisite}
                  copiedMessage="Реквизит скопирован"
                />
              ) : (
                <Typography variant="body1">—</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                UTR
              </Typography>
              {transaction.utr ? (
                <CopyableText
                  value={transaction.utr}
                  copiedMessage="UTR скопирован"
                />
              ) : (
                <Typography>—</Typography>
              )}
            </Grid>
            {isAdmin && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  URL для callback
                </Typography>
                <Typography variant="body1">{transaction.callback_url}</Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                ID трекера
              </Typography>
              <CopyableText
                value={transaction.tracker_id || '-'}
                copiedMessage="ID трекера скопирован"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Дополнительная информация
              </Typography>
              <Typography variant="body1">{transaction.extra_info}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Дата создания
              </Typography>
              <Typography variant="body1">
                {formatApiDateTime(transaction.date_create)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Время создания по IST
              </Typography>
              <Typography variant="body1">
                {formatApiDateTimeInTimeZone(transaction.date_create, 'Asia/Kolkata')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Дата обновления
              </Typography>
              <Typography variant="body1">
                {transaction.date_update
                  ? formatApiDateTime(transaction.date_update)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Время закрытия по IST
              </Typography>
              <Typography variant="body1">
                {transaction.date_update
                  ? formatApiDateTimeInTimeZone(transaction.date_update, 'Asia/Kolkata')
                  : '-'}
              </Typography>
            </Grid>
            {canViewAppealFiles && <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 1 }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                  Файлы апелляций
                </Typography>
                {appealFilesLoading && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Загружаем файлы...
                    </Typography>
                  </Stack>
                )}
                {!appealFilesLoading && appealFilesError && (
                  <Typography variant="body2" color="error">
                    Не удалось загрузить файлы апелляций
                  </Typography>
                )}
                {!appealFilesLoading && !appealFilesError && appealFileEntries.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    К этой транзакции файлы апелляций не прикреплены
                  </Typography>
                )}
                {!appealFilesLoading && appealFileEntries.length > 0 && (
                  <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                    {appealFileEntries.map((entry) => {
                      const fileKind = getAppealFileKind(entry.file);
                      const extension = getAppealFileExtension(entry.file);
                      return (
                        <Button
                          key={`${entry.appealId}-${entry.key}`}
                          variant="outlined"
                          onClick={() => {
                            setPreviewFile(entry);
                            setPreviewFileFailed(false);
                          }}
                          sx={{
                            width: 150,
                            minHeight: 112,
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.75,
                            textTransform: 'none',
                          }}
                        >
                          {fileKind === 'image' ? (
                            <Box
                              component="img"
                              src={entry.file.url}
                              alt={`${entry.label}, апелляция ${entry.appealId}`}
                              loading="lazy"
                              sx={{
                                width: '100%',
                                height: 74,
                                objectFit: 'cover',
                                borderRadius: 1,
                                bgcolor: 'background.default',
                              }}
                            />
                          ) : (
                            <Typography variant="h6">{extension}</Typography>
                          )}
                          <Typography variant="caption">
                            {entry.label} · #{entry.appealId}
                          </Typography>
                        </Button>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Grid>}
          </Grid>
        </DialogContent>
        <DialogActions>
          {transaction.status !== Status.SUCCESS && (
            <Tooltip title={canUpdateToSuccessNow ? 'Перевести в успех' : 'Недостаточно прав'}>
              <span>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={() => handleOpenUpdateDialog('success')}
                  disabled={!canUpdateToSuccessNow}
                >
                  Перевести в успех
                </Button>
              </span>
            </Tooltip>
          )}
          {transaction.status !== Status.DECLINED && (
            <Tooltip title={(isSandbox ? canManageSandbox : canUpdateToDecline) ? 'Перевести в отказ' : 'Недостаточно прав'}>
              <span>
                <Button 
                  variant="contained" 
                  color="error"
                  onClick={() => handleOpenUpdateDialog('decline')}
                  disabled={isSandbox ? !canManageSandbox : !canUpdateToDecline}
                >
                  Перевести в отказ
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title={canSendCallback ? 'Отправить колбэк' : 'Недостаточно прав'}>
            <span>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleOpenCallbackDialog}
                disabled={!canSendCallback}
              >
                Отправить колбэк
              </Button>
            </span>
          </Tooltip>
          <Button onClick={handleClose}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!previewFile}
        onClose={closeFilePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {previewFile?.label} — апелляция #{previewFile?.appealId}
        </DialogTitle>
        <DialogContent>
          {previewFile && previewFileKind === 'image' && !previewFileFailed && (
            <Box
              component="img"
              src={previewFile.file.url}
              alt={`${previewFile.label}, апелляция ${previewFile.appealId}`}
              onError={() => setPreviewFileFailed(true)}
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '75vh',
                mx: 'auto',
                objectFit: 'contain',
              }}
            />
          )}
          {previewFile && previewFileKind === 'video' && (
            <Box
              component="video"
              src={previewFile.file.url}
              controls
              sx={{ display: 'block', width: '100%', maxHeight: '75vh', bgcolor: 'black' }}
            />
          )}
          {previewFile && previewFileKind === 'pdf' && (
            <Box
              component="iframe"
              src={previewFile.file.url}
              title={`${previewFile.label}, апелляция ${previewFile.appealId}`}
              sx={{ width: '100%', height: '75vh', border: 0, bgcolor: 'background.default' }}
            />
          )}
          {previewFile && (
            ['archive', 'download'].includes(previewFileKind) ||
            (previewFileKind === 'image' && previewFileFailed)
          ) && (
            <Typography>
              Предпросмотр для этого формата недоступен. Откройте файл в новой вкладке.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {previewFile && (
            <Button
              href={previewFile.file.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Открыть оригинал
            </Button>
          )}
          <Button onClick={closeFilePreview}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={updateDialogOpen} 
        onClose={handleCloseUpdateDialog} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
        disableEnforceFocus
      >
        <DialogTitle>
          {updateType === 'success' ? 'Перевод в успех' : 'Перевод в отказ'}
        </DialogTitle>
        <DialogContent>
          {!isSandbox && <TextField
            fullWidth
            label="Сумма"
            type="number"
            value={amount}
            onChange={handleAmountChange}
            margin="normal"
            error={!!amountError}
            helperText={amountError}
            inputProps={{
              min: 0.01,
              max: 1000000000,
              step: 0.01
            }}
          />}
          {isSandbox && (
            <Typography sx={{ mt: 2 }}>
              Статус sandbox-заявки будет изменён, после чего callback автоматически отправится мерчанту.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog}>Отмена</Button>
          <Button 
            onClick={handleUpdate} 
            variant="contained" 
            color={updateType === 'success' ? 'success' : 'error'}
          >
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={callbackDialogOpen} 
        onClose={handleCloseCallbackDialog} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
        disableEnforceFocus
      >
        <DialogTitle>Отправка колбэка</DialogTitle>
        <DialogContent>
          {isAdmin ? (
            <TextField
              fullWidth
              label="URL для колбэка"
              value={callbackUrl}
              onChange={(e) => setCallbackUrl(e.target.value)}
              margin="normal"
              placeholder="https://example.com/callback"
            />
          ) : (
            <Typography sx={{ mt: 2 }}>
              Колбэк будет отправлен на сохранённый URL транзакции.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCallbackDialog}>Отмена</Button>
          <Button 
            onClick={handleSendCallback} 
            variant="contained" 
            color="primary"
          >
            Отправить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransactionDetailsDialog; 
